import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { Sparkles, Loader2, XCircle, ArrowLeft } from "lucide-react";

export default function MagicLinkVerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { verifyMagicLink, user } = useAuth();
  const isEn = (i18n.language || "fr").startsWith("en");
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(isEn ? "Verifying the link…" : "Vérification du lien en cours…");
  const fired = useRef(false);

  useEffect(() => {
    if (user) {
      navigate("/", { replace: true });
      return;
    }
    if (fired.current) return;
    fired.current = true;

    const token = searchParams.get("token");
    if (!token) {
      setError(isEn ? "No login token in the URL." : "Aucun jeton de connexion dans l'URL.");
      setStatus(null);
      return;
    }
    (async () => {
      try {
        await verifyMagicLink(token);
        setStatus(isEn ? "Login successful. Redirecting…" : "Connexion réussie. Redirection…");
        setTimeout(() => navigate("/", { replace: true }), 600);
      } catch (err) {
        setError(err?.response?.data?.detail || (isEn ? "Invalid or expired link." : "Lien invalide ou expiré."));
        setStatus(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-soft p-6">
      <div className="w-full max-w-md bg-white rounded-2xl border border-border shadow-sm p-8" data-testid="verify-card">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-full bg-apollo-orange flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading font-black text-base tracking-tight text-text-primary">APOLLO</div>
            <div className="font-heading font-black text-2xl tracking-tighter text-apollo-orange -mt-1">DELPHI</div>
          </div>
        </div>

        {status && !error && (
          <div className="flex items-center gap-3 text-text-primary" data-testid="verify-status">
            <Loader2 className="w-5 h-5 animate-spin text-apollo-orange" />
            <span className="text-base font-medium">{status}</span>
          </div>
        )}

        {error && (
          <div data-testid="verify-error">
            <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 mb-5">
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-900">{isEn ? "Invalid or expired link" : "Lien invalide ou expiré"}</p>
                <p className="text-sm text-red-800 mt-1">{error}</p>
              </div>
            </div>
            <Link
              to="/login"
              data-testid="verify-back-login"
              className="inline-flex items-center gap-2 text-sm font-semibold text-apollo-orange-dark hover:underline"
            >
              <ArrowLeft className="w-4 h-4" /> {isEn ? "Request a new link" : "Demander un nouveau lien"}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
